import React, { useEffect, useState } from "react";
import {
  collection,
  getDocs,
  addDoc,
  deleteDoc,
  doc,
  serverTimestamp,
} from "firebase/firestore";

import { db } from "../firebase";
import "./AdminCourses.css";

function AdminCourses() {
  const [courses, setCourses] = useState([]);

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const [showForm, setShowForm] = useState(false);

  const [status, setStatus] = useState("");

  const [form, setForm] = useState({
    titleAr: "",
    titleEn: "",
    descriptionAr: "",
    descriptionEn: "",
    categoryAr: "",
    categoryEn: "",
  });

  const [image, setImage] = useState(null);
  const [preview, setPreview] = useState("");

  // =====================================================
  // RESET FORM
  // =====================================================

  const resetForm = () => {
    setForm({
      titleAr: "",
      titleEn: "",
      descriptionAr: "",
      descriptionEn: "",
      categoryAr: "",
      categoryEn: "",
    });

    setImage(null);
    setPreview("");
    setStatus("");
  };

  // =====================================================
  // LOAD COURSES
  // =====================================================

  const getCourses = async () => {
    try {
      setLoading(true);

      const coursesRef = collection(db, "courses");

      const snapshot = await getDocs(coursesRef);

      const data = snapshot.docs.map((item) => ({
        id: item.id,
        ...item.data(),
      }));

      setCourses(data);
    } catch (error) {
      console.error("LOAD COURSES ERROR:", error);

      let message = "حدث خطأ أثناء تحميل الدورات.";

      if (error?.code === "permission-denied") {
        message =
          "ليس لديك صلاحية للوصول إلى الدورات في Firebase.\n\n" +
          "تحققي من Firestore Security Rules.";
      } else if (error?.message) {
        message += `\n\n${error.message}`;
      }

      alert(message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    getCourses();
  }, []);

  // =====================================================
  // FORM CHANGE
  // =====================================================

  const handleChange = (e) => {
    const { name, value } = e.target;

    setForm((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  // =====================================================
  // IMAGE → BASE64
  // =====================================================

  const convertImageToBase64 = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();

      reader.onload = (event) => {
        const img = new Image();

        img.onload = () => {
          const canvas = document.createElement("canvas");

          const maxWidth = 800;
          const maxHeight = 500;

          let width = img.width;
          let height = img.height;

          // Resize width
          if (width > maxWidth) {
            height = (height * maxWidth) / width;
            width = maxWidth;
          }

          // Resize height
          if (height > maxHeight) {
            width = (width * maxHeight) / height;
            height = maxHeight;
          }

          canvas.width = Math.round(width);
          canvas.height = Math.round(height);

          const ctx = canvas.getContext("2d");

          if (!ctx) {
            reject(new Error("تعذر معالجة الصورة."));
            return;
          }

          ctx.drawImage(
            img,
            0,
            0,
            canvas.width,
            canvas.height
          );

          // ضغط الصورة
          const base64 = canvas.toDataURL(
            "image/jpeg",
            0.55
          );

          resolve(base64);
        };

        img.onerror = () => {
          reject(new Error("تعذر فتح الصورة."));
        };

        img.src = event.target.result;
      };

      reader.onerror = () => {
        reject(new Error("تعذر قراءة الصورة."));
      };

      reader.readAsDataURL(file);
    });
  };

  // =====================================================
  // SELECT IMAGE
  // =====================================================

  const handleImage = (e) => {
    const file = e.target.files?.[0];

    if (!file) return;

    if (!file.type.startsWith("image/")) {
      alert("اختاري صورة فقط 📷");
      return;
    }

    // 10MB قبل الضغط
    if (file.size > 10 * 1024 * 1024) {
      alert(
        "الصورة كبيرة جدًا.\n\nاختاري صورة حجمها أقل من 10MB."
      );
      return;
    }

    setImage(file);

    const objectUrl = URL.createObjectURL(file);

    setPreview(objectUrl);
  };

  // =====================================================
  // ADD COURSE
  // =====================================================

  const addCourse = async (e) => {
    e.preventDefault();

    if (saving) return;

    // ===================================================
    // VALIDATION
    // ===================================================

    const requiredFields = [
      form.titleAr,
      form.titleEn,
      form.descriptionAr,
      form.descriptionEn,
      form.categoryAr,
      form.categoryEn,
    ];

    const hasEmptyField = requiredFields.some(
      (field) => !field.trim()
    );

    if (hasEmptyField) {
      alert(
        "عبّي جميع المعلومات المطلوبة ❤️"
      );
      return;
    }

    if (!image) {
      alert("اختاري صورة للدورة 📷");
      return;
    }

    try {
      setSaving(true);

      // =================================================
      // IMAGE
      // =================================================

      setStatus("جاري تجهيز الصورة...");

      const imageBase64 =
        await convertImageToBase64(image);

      // =================================================
      // CHECK IMAGE SIZE
      // =================================================

      const imageSizeMB =
        (imageBase64.length * 3) /
        4 /
        1024 /
        1024;

      console.log(
        "Compressed image size:",
        imageSizeMB.toFixed(2),
        "MB"
      );

      /*
       * Firestore document limit is around 1 MB.
       * Base64 images can easily exceed that.
       */

      if (imageBase64.length > 900000) {
        alert(
          "الصورة بعد الضغط ما زالت كبيرة جدًا.\n\n" +
          "اختاري صورة أصغر أو صورة أبسط."
        );

        setSaving(false);
        setStatus("");

        return;
      }

      // =================================================
      // COURSE DATA
      // =================================================

      setStatus("جاري حفظ الدورة...");

      const courseData = {
        titleAr: form.titleAr.trim(),

        titleEn: form.titleEn.trim(),

        descriptionAr:
          form.descriptionAr.trim(),

        descriptionEn:
          form.descriptionEn.trim(),

        categoryAr:
          form.categoryAr.trim(),

        categoryEn:
          form.categoryEn.trim(),

        image: imageBase64,

        createdAt: serverTimestamp(),
      };

      // =================================================
      // FIRESTORE
      // =================================================

      const coursesRef = collection(
        db,
        "courses"
      );

      const result = await addDoc(
        coursesRef,
        courseData
      );

      // =================================================
      // ADD LOCALLY IMMEDIATELY
      // =================================================

      const newCourse = {
        id: result.id,
        ...courseData,
        createdAt: new Date(),
      };

      setCourses((prev) => [
        newCourse,
        ...prev,
      ]);

      // =================================================
      // RESET
      // =================================================

      resetForm();

      setShowForm(false);

      alert(
        "🎉 تمت إضافة الدورة بنجاح!"
      );
    } catch (error) {
      console.error(
        "ADD COURSE ERROR:",
        error
      );

      setStatus("");

      // Firebase permission
      if (
        error?.code ===
        "permission-denied"
      ) {
        alert(
          "❌ ليس لديك صلاحية لإضافة دورة.\n\n" +
            "المشكلة من Firestore Security Rules."
        );

        return;
      }

      // Firebase unavailable
      if (
        error?.code ===
        "unavailable"
      ) {
        alert(
          "❌ تعذر الاتصال بـ Firebase.\n\n" +
            "تأكدي من الإنترنت وحاولي مرة أخرى."
        );

        return;
      }

      // Generic error
      alert(
        "❌ حدث خطأ أثناء حفظ الدورة.\n\n" +
          (error?.message ||
            "خطأ غير معروف")
      );
    } finally {
      setSaving(false);
    }
  };

  // =====================================================
  // DELETE COURSE
  // =====================================================

  const deleteCourse = async (id) => {
    if (!id) return;

    const confirmed = window.confirm(
      "هل أنتِ متأكدة من حذف هذه الدورة؟\n\nلا يمكن التراجع عن هذه العملية."
    );

    if (!confirmed) return;

    try {
      setStatus("جاري حذف الدورة...");

      await deleteDoc(
        doc(
          db,
          "courses",
          id
        )
      );

      setCourses((prev) =>
        prev.filter(
          (course) =>
            course.id !== id
        )
      );

      setStatus("");

      alert(
        "🗑️ تم حذف الدورة بنجاح."
      );
    } catch (error) {
      console.error(
        "DELETE COURSE ERROR:",
        error
      );

      setStatus("");

      if (
        error?.code ===
        "permission-denied"
      ) {
        alert(
          "❌ ليس لديك صلاحية لحذف الدورة.\n\n" +
            "تحققي من Firestore Security Rules."
        );

        return;
      }

      alert(
        "❌ حدث خطأ أثناء حذف الدورة.\n\n" +
          (error?.message ||
            "خطأ غير معروف")
      );
    }
  };

  // =====================================================
  // CLOSE FORM
  // =====================================================

  const closeForm = () => {
    if (saving) return;

    setShowForm(false);

    resetForm();
  };

  // =====================================================
  // OPEN FORM
  // =====================================================

  const openForm = () => {
    if (saving) return;

    resetForm();

    setShowForm(true);
  };

  // =====================================================
  // UI
  // =====================================================

  return (
    <div className="admin-courses">

      {/* =================================================
          HEADER
      ================================================= */}

      <header className="courses-header">

        <div className="courses-header-text">

          <span className="courses-tag">
            MADAD ADMIN
          </span>

          <h1>
            إدارة الدورات
          </h1>

          <p>
            أضيفي وعدّلي الدورات والبرامج
            التعليمية الخاصة بمركز مداد.
          </p>

        </div>

        <button
          type="button"
          className="add-course-button"
          onClick={() =>
            showForm
              ? closeForm()
              : openForm()
          }
          disabled={saving}
        >
          <span>
            {showForm ? "×" : "＋"}
          </span>

          {showForm
            ? "إغلاق"
            : "إضافة دورة"}
        </button>

      </header>

      {/* =================================================
          FORM
      ================================================= */}

      {showForm && (

        <section className="course-form-section">

          <div className="form-title">

            <div>
              <span>
                NEW COURSE
              </span>

              <h2>
                إضافة دورة جديدة
              </h2>
            </div>

            <button
              type="button"
              className="close-form"
              onClick={closeForm}
              disabled={saving}
            >
              ×
            </button>

          </div>

          <form
            onSubmit={addCourse}
          >

            {/* =================================================
                ARABIC
            ================================================= */}

            <div className="form-group">

              <div className="form-group-title">

                <span>
                  ع
                </span>

                <div>
                  <h3>
                    المعلومات بالعربي
                  </h3>

                  <p>
                    معلومات الدورة باللغة العربية
                  </p>
                </div>

              </div>

              <div className="form-grid">

                {/* TITLE */}

                <div className="input-box">

                  <label>
                    اسم الدورة *
                  </label>

                  <input
                    type="text"
                    name="titleAr"
                    value={form.titleAr}
                    onChange={handleChange}
                    placeholder="مثال: دورة البرمجة"
                    disabled={saving}
                    required
                  />

                </div>

                {/* CATEGORY */}

                <div className="input-box">

                  <label>
                    التصنيف *
                  </label>

                  <input
                    type="text"
                    name="categoryAr"
                    value={form.categoryAr}
                    onChange={handleChange}
                    placeholder="مثال: برمجة"
                    disabled={saving}
                    required
                  />

                </div>

                {/* DESCRIPTION */}

                <div className="input-box full">

                  <label>
                    وصف الدورة *
                  </label>

                  <textarea
                    name="descriptionAr"
                    value={form.descriptionAr}
                    onChange={handleChange}
                    placeholder="اكتبي وصفًا مختصرًا عن الدورة..."
                    rows="4"
                    disabled={saving}
                    required
                  />

                </div>

              </div>

            </div>

            {/* =================================================
                ENGLISH
            ================================================= */}

            <div className="form-group english-group">

              <div className="form-group-title">

                <span>
                  EN
                </span>

                <div>
                  <h3>
                    English Information
                  </h3>

                  <p>
                    Course information in English
                  </p>
                </div>

              </div>

              <div className="form-grid">

                {/* TITLE */}

                <div className="input-box">

                  <label>
                    Course Name *
                  </label>

                  <input
                    type="text"
                    name="titleEn"
                    value={form.titleEn}
                    onChange={handleChange}
                    placeholder="Example: Programming Course"
                    disabled={saving}
                    required
                  />

                </div>

                {/* CATEGORY */}

                <div className="input-box">

                  <label>
                    Category *
                  </label>

                  <input
                    type="text"
                    name="categoryEn"
                    value={form.categoryEn}
                    onChange={handleChange}
                    placeholder="Example: Programming"
                    disabled={saving}
                    required
                  />

                </div>

                {/* DESCRIPTION */}

                <div className="input-box full">

                  <label>
                    Course Description *
                  </label>

                  <textarea
                    name="descriptionEn"
                    value={form.descriptionEn}
                    onChange={handleChange}
                    placeholder="Write a short description..."
                    rows="4"
                    disabled={saving}
                    required
                  />

                </div>

              </div>

            </div>

            {/* =================================================
                IMAGE
            ================================================= */}

            <div className="form-group">

              <div className="form-group-title">

                <span>
                  📷
                </span>

                <div>
                  <h3>
                    صورة الدورة
                  </h3>

                  <p>
                    اختاري صورة من جهازك
                  </p>
                </div>

              </div>

              <label className="image-upload">

                <input
                  type="file"
                  accept="image/jpeg,image/png,image/webp"
                  onChange={handleImage}
                  disabled={saving}
                />

                <div className="upload-icon">
                  ↑
                </div>

                <strong>
                  اضغطي لاختيار صورة
                </strong>

                <span>
                  JPG, PNG, WEBP — حتى 10MB
                </span>

              </label>

              {/* =================================================
                  PREVIEW
              ================================================= */}

              {preview && (

                <div className="image-preview-container">

                  <span>
                    معاينة الصورة
                  </span>

                  <img
                    src={preview}
                    alt="Course Preview"
                  />

                </div>

              )}

            </div>

            {/* =================================================
                STATUS
            ================================================= */}

            {status && (

              <div className="save-status">

                <span className="loading-dot">
                  ●
                </span>

                {status}

              </div>

            )}

            {/* =================================================
                ACTIONS
            ================================================= */}

            <div className="form-actions">

              <button
                type="submit"
                className="save-course"
                disabled={saving}
              >

                {saving ? (
                  <>
                    <span className="spinner">
                      ◌
                    </span>

                    جاري الحفظ...
                  </>
                ) : (
                  <>
                    <span>
                      ✓
                    </span>

                    حفظ الدورة
                  </>
                )}

              </button>

              <button
                type="button"
                className="cancel-course"
                onClick={closeForm}
                disabled={saving}
              >
                إلغاء
              </button>

            </div>

          </form>

        </section>
      )}

      {/* =================================================
          COURSES LIST
      ================================================= */}

      <section className="courses-list">

        <div className="list-heading">

          <div>

            <span>
              CONTENT
            </span>

            <h2>
              الدورات الموجودة
            </h2>

          </div>

          <div className="courses-count">

            {courses.length}

            <small>
              دورة
            </small>

          </div>

        </div>

        {/* =================================================
            LOADING
        ================================================= */}

        {loading && (

          <div className="courses-loading">

            <div className="big-spinner">
              ◌
            </div>

            <p>
              جاري تحميل الدورات...
            </p>

          </div>

        )}

        {/* =================================================
            EMPTY
        ================================================= */}

        {!loading &&
          courses.length === 0 && (

            <div className="empty-courses">

              <div>
                📚
              </div>

              <h3>
                لا توجد دورات حتى الآن
              </h3>

              <p>
                ابدئي بإضافة أول دورة للمركز.
              </p>

              <button
                type="button"
                onClick={openForm}
                disabled={saving}
              >
                ＋ إضافة أول دورة
              </button>

            </div>

          )}

        {/* =================================================
            COURSES
        ================================================= */}

        {!loading &&
          courses.length > 0 && (

            <div className="courses-admin-grid">

              {courses.map(
                (course) => (

                  <article
                    className="course-admin-card"
                    key={course.id}
                  >

                    {/* IMAGE */}

                    <div className="course-card-image">

                      {course.image ? (

                        <img
                          src={course.image}
                          alt={
                            course.titleAr ||
                            "Course"
                          }
                        />

                      ) : (

                        <div className="no-image">
                          📚
                        </div>

                      )}

                      <span className="card-category">

                        {course.categoryAr ||
                          "دورة"}

                      </span>

                    </div>

                    {/* CONTENT */}

                    <div className="course-card-content">

                      <h3>
                        {course.titleAr ||
                          "بدون اسم"}
                      </h3>

                      <p className="course-description">

                        {course.descriptionAr ||
                          "لا يوجد وصف لهذه الدورة."}

                      </p>

                      <div className="course-english">

                        <span>
                          EN
                        </span>

                        <strong>

                          {course.titleEn ||
                            "—"}

                        </strong>

                      </div>

                      <div className="course-card-footer">

                        <span className="course-id">

                          #
                          {course.id
                            ? course.id.slice(
                                0,
                                6
                              )
                            : "------"}

                        </span>

                        <button
                          type="button"
                          className="delete-course"
                          onClick={() =>
                            deleteCourse(
                              course.id
                            )
                          }
                          disabled={saving}
                        >
                          🗑️ حذف
                        </button>

                      </div>

                    </div>

                  </article>

                )
              )}

            </div>

          )}

      </section>

    </div>
  );
}

export default AdminCourses;